import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/auth';
import { StitchedPMark } from '../components/StitchedPMark';

const COLOR_SWATCHES = [
  '#1E3A2A', // forest
  '#A0392C', // brick
  '#D4A846', // gold
  '#2C4A6B', // navy
  '#6B3A5C', // plum
  '#8B5A2B', // ochre
];

export function AuthScreen() {
  const { status, user, refresh } = useSession();

  if (status === 'loading') {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (status === 'signed_out' || !user) {
    return <SignInForm />;
  }

  if (status === 'ready') {
    return <Navigate to="/" replace />;
  }

  const meta = user.user_metadata as {
    invited_to_family_id?: string;
    invited_role?: string;
    invited_family_name?: string;
  } | null;
  // Older invites may still have invited_role: 'member' in metadata; treat
  // both the same since accept_invite always provisions as leader now.
  if (meta?.invited_to_family_id && meta.invited_role) {
    return (
      <AcceptInviteForm
        familyName={meta.invited_family_name ?? ''}
        onDone={refresh}
      />
    );
  }

  return <FamilySetup userEmail={user.email ?? ''} onDone={refresh} />;
}

// ─────────────────────────────────────────────────────────────────────
function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'JetBrains Mono',
        fontSize: 11,
        color: 'var(--ink-soft)',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function SignInForm() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg('');
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
    setBusy(false);
    if (error) {
      setErrorMsg(error.message);
    } else {
      setPhase('code');
      setCode('');
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg('');
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: 'email',
    });
    setBusy(false);
    if (error) {
      setErrorMsg(
        error.message.toLowerCase().includes('expired') || error.message.toLowerCase().includes('invalid')
          ? 'That code is expired or invalid. Tap "Resend code" to get a new one.'
          : error.message
      );
    }
    // Success: AuthProvider's onAuthStateChange will swap to FamilySetup
  }

  async function resendCode() {
    setBusy(true);
    setErrorMsg('');
    setCode('');
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: `${window.location.origin}/login` },
    });
    setBusy(false);
    if (error) setErrorMsg(error.message);
  }

  return (
    <Page>
      <StitchedPMark size={88} />
      <Headline>PATCH LEGACY</Headline>
      <SubHeadline>★ FAMILY TRADITION ★</SubHeadline>

      {phase === 'code' ? (
        <>
          <p
            style={{
              fontFamily: 'Newsreader, serif',
              fontSize: 17,
              color: 'var(--ink-soft)',
              maxWidth: 360,
              margin: '8px 0 16px',
              lineHeight: 1.45,
              textAlign: 'center',
            }}
          >
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below.
          </p>
          <form
            onSubmit={verifyCode}
            style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              autoFocus
              maxLength={10}
              placeholder="------"
              aria-label="6-digit verification code"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{
                ...textInputStyle,
                fontFamily: 'JetBrains Mono',
                letterSpacing: '0.4em',
                textAlign: 'center',
                fontSize: 22,
              }}
            />
            <PrimaryButton type="submit" disabled={busy || code.length < 6}>
              {busy ? 'VERIFYING…' : 'VERIFY CODE'}
            </PrimaryButton>
            {errorMsg && (
              <div
                style={{
                  fontFamily: 'JetBrains Mono',
                  fontSize: 11,
                  color: 'var(--brick)',
                  textAlign: 'center',
                  letterSpacing: '0.04em',
                }}
              >
                {errorMsg}
              </div>
            )}
            <button type="button" onClick={resendCode} disabled={busy} style={textButtonStyle}>
              Resend code
            </button>
            <button
              type="button"
              onClick={() => {
                setPhase('email');
                setCode('');
                setErrorMsg('');
              }}
              style={textButtonStyle}
            >
              Use a different email
            </button>
          </form>
        </>
      ) : (
        <>
          <p
            style={{
              fontFamily: 'Newsreader, serif',
              fontSize: 17,
              color: 'var(--ink-soft)',
              maxWidth: 360,
              margin: '8px 0 24px',
              lineHeight: 1.45,
              textAlign: 'center',
            }}
          >
            Sign in with your email. We'll send you a 6-digit code.
          </p>
          <form
            onSubmit={sendCode}
            style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <input
              type="email"
              required
              autoFocus
              placeholder="you@example.com"
              aria-label="Email address"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={textInputStyle}
            />
            <PrimaryButton type="submit" disabled={busy}>
              {busy ? 'SENDING…' : 'SEND CODE'}
            </PrimaryButton>
            {errorMsg && (
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--brick)' }}>
                {errorMsg}
              </div>
            )}
          </form>
        </>
      )}
    </Page>
  );
}

// ─────────────────────────────────────────────────────────────────────
function FamilySetup({ userEmail, onDone }: { userEmail: string; onDone: () => Promise<void> }) {
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose');

  if (mode === 'choose') {
    return (
      <Page>
        <StitchedPMark size={72} />
        <Headline>WELCOME</Headline>
        <p
          style={{
            fontFamily: 'Newsreader, serif',
            fontSize: 16,
            color: 'var(--ink-soft)',
            margin: '8px 0 4px',
            textAlign: 'center',
          }}
        >
          Signed in as <strong>{userEmail}</strong>
        </p>
        <p
          style={{
            fontFamily: 'Newsreader, serif',
            fontSize: 17,
            color: 'var(--ink)',
            maxWidth: 360,
            margin: '8px 0 24px',
            lineHeight: 1.45,
            textAlign: 'center',
          }}
        >
          Start a new troop or join one with an invite code.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 340 }}>
          <PrimaryButton onClick={() => setMode('create')}>START A TROOP</PrimaryButton>
          <SecondaryButton onClick={() => setMode('join')}>JOIN WITH CODE</SecondaryButton>
        </div>
        <button
          onClick={() => supabase.auth.signOut()}
          style={{
            marginTop: 20,
            background: 'none',
            border: 'none',
            fontFamily: 'JetBrains Mono',
            fontSize: 10,
            letterSpacing: '0.14em',
            color: 'var(--ink-soft)',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Sign out
        </button>
      </Page>
    );
  }

  if (mode === 'create') {
    return <CreateFamilyForm onCancel={() => setMode('choose')} onDone={onDone} />;
  }

  return <JoinFamilyForm onCancel={() => setMode('choose')} onDone={onDone} />;
}

// ─────────────────────────────────────────────────────────────────────
function CreateFamilyForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => Promise<void> }) {
  const [troopNumber, setTroopNumber] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [color, setColor] = useState(COLOR_SWATCHES[0]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg('');
    const parsed = Number.parseInt(troopNumber, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setBusy(false);
      setErrorMsg('Pick a positive troop number.');
      return;
    }
    const { error } = await supabase.rpc('create_family', {
      p_troop_number: parsed,
      display_name: displayName.trim(),
      member_color: color,
    });
    if (error) {
      setBusy(false);
      setErrorMsg(
        error.message === 'troop_number_taken'
          ? 'That troop number is already in use. Pick another.'
          : error.message
      );
      return;
    }
    await onDone();
  }

  return (
    <Page>
      <Headline>START A TROOP</Headline>
      <form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
        <Field label="TROOP NUMBER">
          <input
            type="number"
            inputMode="numeric"
            required
            min={1}
            placeholder="40"
            value={troopNumber}
            onChange={(e) => setTroopNumber(e.target.value.replace(/[^0-9]/g, ''))}
            style={{ ...textInputStyle, fontFamily: 'JetBrains Mono', letterSpacing: '0.12em', textAlign: 'center' }}
          />
        </Field>
        <Field label="YOUR NAME">
          <input
            type="text"
            required
            placeholder="John"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={textInputStyle}
          />
        </Field>
        <Field label="YOUR COLOR">
          <ColorPicker value={color} onChange={setColor} />
        </Field>
        <PrimaryButton type="submit" disabled={busy}>
          {busy ? 'STARTING…' : 'START TROOP'}
        </PrimaryButton>
        <button type="button" onClick={onCancel} style={textButtonStyle}>
          Back
        </button>
        {errorMsg && (
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--brick)' }}>{errorMsg}</div>
        )}
      </form>
    </Page>
  );
}

// ─────────────────────────────────────────────────────────────────────
function JoinFamilyForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => Promise<void> }) {
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [color, setColor] = useState(COLOR_SWATCHES[1]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg('');
    const { error } = await supabase.rpc('join_family', {
      code: code.trim().toUpperCase(),
      display_name: displayName.trim(),
      member_color: color,
    });
    if (error) {
      setBusy(false);
      setErrorMsg(
        error.message.includes('family_not_found')
          ? 'No troop found with that invite code.'
          : error.message
      );
      return;
    }
    await onDone();
  }

  return (
    <Page>
      <Headline>JOIN A TROOP</Headline>
      <form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
        <Field label="INVITE CODE">
          <input
            type="text"
            required
            placeholder="ABC123"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            style={{ ...textInputStyle, fontFamily: 'JetBrains Mono', letterSpacing: '0.2em', textAlign: 'center', textTransform: 'uppercase' }}
          />
        </Field>
        <Field label="YOUR NAME">
          <input
            type="text"
            required
            placeholder="John"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={textInputStyle}
          />
        </Field>
        <Field label="YOUR COLOR">
          <ColorPicker value={color} onChange={setColor} />
        </Field>
        <PrimaryButton type="submit" disabled={busy}>
          {busy ? 'JOINING…' : 'JOIN TROOP'}
        </PrimaryButton>
        <button type="button" onClick={onCancel} style={textButtonStyle}>
          Back
        </button>
        {errorMsg && (
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--brick)' }}>{errorMsg}</div>
        )}
      </form>
    </Page>
  );
}

// ─────────────────────────────────────────────────────────────────────
function AcceptInviteForm({
  familyName,
  onDone,
}: {
  familyName: string;
  onDone: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState('');
  const [color, setColor] = useState(COLOR_SWATCHES[1]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMsg('');
    const { error } = await supabase.rpc('accept_invite', {
      p_display_name: displayName.trim(),
      p_member_color: color,
    });
    if (error) {
      setBusy(false);
      setErrorMsg(error.message);
      return;
    }
    await onDone();
  }

  const familyLabel = familyName.trim();
  return (
    <Page>
      <SubHeadline>★ YOU'RE INVITED ★</SubHeadline>
      <Headline>{familyLabel ? `JOIN ${familyLabel.toUpperCase()}` : 'JOIN YOUR TROOP'}</Headline>
      <p
        style={{
          fontFamily: 'Newsreader, serif',
          fontSize: 17,
          color: 'var(--ink)',
          maxWidth: 360,
          margin: '8px 0 12px',
          lineHeight: 1.45,
          textAlign: 'center',
        }}
      >
        You'll join as a <strong>leader</strong>. Pick how the troop will see you.
      </p>
      <form onSubmit={onSubmit} style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 14, marginTop: 6 }}>
        <Field label="YOUR NAME">
          <input
            type="text"
            required
            placeholder="John"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={textInputStyle}
            autoFocus
          />
        </Field>
        <Field label="YOUR COLOR">
          <ColorPicker value={color} onChange={setColor} />
        </Field>
        <PrimaryButton type="submit" disabled={busy}>
          {busy ? 'JOINING…' : 'JOIN TROOP'}
        </PrimaryButton>
        {errorMsg && (
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--brick)' }}>{errorMsg}</div>
        )}
      </form>
      <button
        onClick={() => supabase.auth.signOut()}
        style={{
          marginTop: 20,
          background: 'none',
          border: 'none',
          fontFamily: 'JetBrains Mono',
          fontSize: 10,
          letterSpacing: '0.14em',
          color: 'var(--ink-soft)',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        Sign out
      </button>
    </Page>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Reusable bits
// ─────────────────────────────────────────────────────────────────────
function Page({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--paper)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        textAlign: 'center',
        gap: 12,
        overflow: 'auto',
      }}
    >
      {children}
    </div>
  );
}

function Headline({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'Bevan',
        fontSize: 30,
        color: 'var(--ink)',
        letterSpacing: '0.04em',
        lineHeight: 1,
      }}
    >
      {children}
    </div>
  );
}

function SubHeadline({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'JetBrains Mono',
        fontSize: 11,
        color: 'var(--brick)',
        letterSpacing: '0.18em',
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left' }}>
      <span
        style={{
          fontFamily: 'JetBrains Mono',
          fontSize: 10,
          letterSpacing: '0.14em',
          color: 'var(--ink-soft)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {COLOR_SWATCHES.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          aria-label={`Color ${c}`}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: c,
            border: value === c ? '2.5px solid var(--ink)' : '1.5px solid rgba(20,16,10,0.3)',
            boxShadow: value === c ? '0 0 0 2px var(--cream), 0 0 0 3.5px var(--ink)' : 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        />
      ))}
    </div>
  );
}

const textInputStyle: React.CSSProperties = {
  fontFamily: 'Newsreader, serif',
  fontSize: 17,
  padding: '12px 14px',
  border: '1.5px solid var(--ink)',
  background: 'var(--cream)',
  color: 'var(--ink)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

function PrimaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      style={{
        fontFamily: 'Bevan',
        fontSize: 14,
        letterSpacing: '0.08em',
        padding: '14px 18px',
        border: '1.5px solid var(--ink)',
        background: 'var(--brick)',
        color: 'var(--cream)',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.6 : 1,
        boxShadow: '3px 3px 0 var(--ink)',
        ...props.style,
      }}
    />
  );
}

function SecondaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      style={{
        fontFamily: 'Bevan',
        fontSize: 14,
        letterSpacing: '0.08em',
        padding: '14px 18px',
        border: '1.5px solid var(--ink)',
        background: 'var(--cream)',
        color: 'var(--ink)',
        cursor: 'pointer',
        boxShadow: '3px 3px 0 var(--ink)',
        ...props.style,
      }}
    />
  );
}

const textButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontFamily: 'JetBrains Mono',
  fontSize: 11,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--ink-soft)',
  cursor: 'pointer',
  marginTop: 4,
};
